import { Layers } from "lucide-react"
import { cn } from "@/lib/utils"
import { useCaseCategories } from "@/data/use-cases-data"

interface CategoryTabsProps {
  /** Currently selected category ID, or null for "All" */
  activeCategory: string | null
  /** Callback when a category is selected */
  onCategoryChange: (categoryId: string | null) => void
}

/**
 * Category filter tabs for the Use Cases listing page.
 * Allows filtering use cases by department/industry category.
 */
export function CategoryTabs({ activeCategory, onCategoryChange }: CategoryTabsProps) {
  return (
    <div className="rounded-2xl bg-white/60 px-4 py-4 shadow-sm ring-1 ring-neutral-200/50 backdrop-blur-sm">
      <div className="hide-scrollbar flex gap-3 overflow-x-auto">
        {/* All Categories Tab */}
        <button
          onClick={() => onCategoryChange(null)}
          className={cn(
            "flex shrink-0 items-center gap-2.5 rounded-xl px-5 py-3 text-sm font-medium transition-all duration-200",
            activeCategory === null
              ? "bg-primary text-white shadow-lg shadow-primary/25"
              : "bg-white text-neutral-600 shadow-sm ring-1 ring-neutral-200 hover:bg-neutral-50 hover:ring-primary/30"
          )}
        >
          <Layers className="h-4 w-4" />
          All
        </button>

        {/* Category Tabs */}
        {useCaseCategories.map((category) => {
          const Icon = category.icon
          const isActive = activeCategory === category.id
          return (
            <button
              key={category.id}
              onClick={() => onCategoryChange(category.id)}
              className={cn(
                "flex shrink-0 items-center gap-2.5 rounded-xl px-5 py-3 text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-primary text-white shadow-lg shadow-primary/25"
                  : "bg-white text-neutral-600 shadow-sm ring-1 ring-neutral-200 hover:bg-neutral-50 hover:ring-primary/30"
              )}
            >
              <Icon className="h-4 w-4" />
              {category.label}
            </button>
          )
        })}
      </div>

      {/* Custom scrollbar hide styles */}
      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}
